import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Select as AntSelect } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import UiKit from '../index';
import { Skeleton } from './Skeleton';
import { ValidationContext } from './Validation';

type SelectValueType = string | number | null;
type SelectSizeType = 'large' | 'middle' | 'small';
type FilterOption<ValueType extends SelectValueType> = {
  label: string;
  value: ValueType;
  options?: Omit<FilterOption<ValueType>, 'options'>[];
};
type SelectOption<ValueType extends SelectValueType> = {
  label: string;
  value: ValueType;
  children?: Omit<SelectOption<SelectValueType>, 'children'>[];
};

enum TypeMultiSelect {
  GROUP,
  SINGLE,
}

interface MultiSelectProps<ValueType extends SelectValueType> {
  values: SelectValueType[] | undefined;
  showSearch?: boolean;
  onChange?: (values: SelectValueType[]) => void;
  onSearch?: (values: string) => void;
  optionFilterProp?: string;
  options: SelectOption<ValueType>[];
  disabled?: boolean;
  filter?: (input: string, option: SelectOption<ValueType> | undefined) => boolean;
  maxTagCount?: number | 'responsive';
  dropdownRender?: (node: React.ReactElement) => React.ReactElement;
  className?: string;
  placeholder?: string;
  size?: SelectSizeType;
  onPageScrolled?: () => void;
  notFoundContent?: React.ReactNode;
  showSelectAll?: boolean;
  selectAllText?: string;
  defaultValue?: ValueType[];
  defaultSelectedAll?: boolean;
  loading?: boolean;
  searchValue?: string;
}

const Option = AntSelect.Option;
const OptionGroup = AntSelect.OptGroup;

function filterOptionsFn<ValueType extends SelectValueType>(
  input: string,
  opts: SelectOption<ValueType>[],
  filter?: (input: string, option: SelectOption<ValueType> | undefined) => boolean,
): SelectOption<ValueType>[] {
  if (!input) return opts;

  const result: SelectOption<ValueType>[] = [];

  opts.forEach(option => {
    if (option.children) {
      const filteredChildren = option.children.filter(child => {
        if (filter) {
          return filter(input, option);
        }
        return child.label.toLowerCase().includes(input.toLowerCase());
      });

      if (filteredChildren.length > 0) {
        result.push({ ...option, children: filteredChildren });
      }
    } else if (filter) {
      if (filter(input, option)) {
        result.push(option);
      }
    } else if (option.label.toLowerCase().includes(input.toLowerCase())) {
      result.push(option);
    }
  });

  return result;
}

function MultiSelect<ValueType extends SelectValueType>({
  values = [],
  showSearch,
  onChange,
  onSearch,
  onPageScrolled,
  options,
  disabled,
  filter,
  maxTagCount,
  dropdownRender,
  className,
  size,
  placeholder,
  notFoundContent,
  showSelectAll,
  selectAllText,
  defaultValue,
  defaultSelectedAll,
  loading,
  searchValue,
}: MultiSelectProps<ValueType>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);
  const [searchInput, setSearchInput] = useState(searchValue || '');

  const filteredOptions = useMemo(
    () => filterOptionsFn(searchInput, options, filter),
    [searchInput, options, filter],
  );

  const children = useMemo(
    () => filteredOptions.flatMap(opt => opt.children ?? []),
    [filteredOptions],
  );

  const typeMultiSelect = useMemo(() => {
    return children.length > 0 ? TypeMultiSelect.GROUP : TypeMultiSelect.SINGLE;
  }, [children.length]);

  const isFilterSelectedAll = useMemo(() => {
    const totalCount =
      typeMultiSelect === TypeMultiSelect.GROUP ? children.length : filteredOptions.length;
    return values.length > 0 && values.length === totalCount;
  }, [values, children.length, filteredOptions.length, typeMultiSelect]);

  const selectedValuesKey = values.join(',');

  const allSelected = useMemo(() => {
    if (typeMultiSelect === TypeMultiSelect.GROUP) {
      return values?.length === children.length;
    }
    return values?.length === filteredOptions.length;
  }, [values?.length, children.length, filteredOptions.length, typeMultiSelect]);

  useEffect(() => {
    if (defaultSelectedAll) {
      onChange?.(['all']);
    }
  }, []);

  useEffect(() => {
    if (searchInput || !showSelectAll || !values?.length || values.includes('all')) return;

    const totalCount = typeMultiSelect === TypeMultiSelect.GROUP ? children.length : options.length;

    if (totalCount > 0 && values.length === totalCount) {
      onChange?.(['all']);
    }
  }, [selectedValuesKey, children.length, options.length, typeMultiSelect]);

  useEffect(() => {
    if (searchValue !== undefined) {
      setSearchInput(searchValue);
    }
  }, [searchValue]);

  const antOnFilterSelectClick = useCallback(
    (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
      event.stopPropagation();

      if (isFilterSelectedAll) {
        onChange?.([]);
      } else {
        const allValues =
          typeMultiSelect === TypeMultiSelect.GROUP
            ? children.map(item => item.value)
            : filteredOptions.map(item => item.value);
        onChange?.(allValues);
      }
    },
    [isFilterSelectedAll, typeMultiSelect, children, filteredOptions, onChange],
  );

  const antOnChange = useCallback(
    (selectedValues: SelectValueType[]) => {
      if (selectedValues.includes('all') && !values?.includes('all')) {
        return onChange?.(allSelected ? [] : ['all']);
      }
      if (selectedValues.includes('all') && values?.includes('all')) {
        const allValues =
          typeMultiSelect === TypeMultiSelect.SINGLE
            ? filteredOptions.map(item => item.value)
            : children.map(child => child.value);
        const deselected = allValues.filter(item => !selectedValues.includes(item));
        return onChange?.(deselected);
      }
      onChange?.(selectedValues);
    },
    [values, typeMultiSelect, filteredOptions, children, onChange, allSelected],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      onSearch?.(value);
    },
    [onSearch],
  );

  const antPopupScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
      const target = event.target as HTMLDivElement;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
        onPageScrolled?.();
      }
    },
    [onPageScrolled],
  );

  if (loading) {
    return <StyledSkeleton className={className} />;
  }

  return (
    <StyledSelect
      mode={'multiple'}
      value={options.length ? values : []}
      onChange={antOnChange}
      onSearch={handleSearch}
      placeholder={placeholder}
      showSearch={showSearch}
      onPopupScroll={antPopupScroll}
      defaultValue={defaultValue ?? defaultSelectedAll ? ['all'] : undefined}
      disabled={disabled}
      status={validateStatus}
      className={className}
      searchValue={searchInput}
      size={size ? size : 'large'}
      maxTagCount={maxTagCount}
      dropdownRender={dropdownRender}
      notFoundContent={notFoundContent}
      filterOption={false}
    >
      {searchInput && !!filteredOptions.length && (
        <Option
          className={allSelected ? 'ant-select-item-option-selected' : ''}
          key={'allFiltered'}
          value={'allFiltered'}
        >
          <OptionContentWrapper onClick={e => antOnFilterSelectClick(e)}>
            <OptionLabel>
              {selectAllText ? selectAllText : 'Все результаты'} (
              {typeMultiSelect === TypeMultiSelect.SINGLE
                ? filteredOptions.length
                : children.length}
              )
            </OptionLabel>
          </OptionContentWrapper>
        </Option>
      )}
      {!searchInput && showSelectAll && !!filteredOptions.length && (
        <Option
          className={allSelected ? 'ant-select-item-option-selected' : ''}
          key={'all'}
          value={'all'}
        >
          <OptionContentWrapper>
            <OptionLabel>
              {selectAllText ? selectAllText : 'Все результаты'} (
              {typeMultiSelect === TypeMultiSelect.SINGLE ? options.length : children.length})
            </OptionLabel>
          </OptionContentWrapper>
        </Option>
      )}

      {filteredOptions.map(option => {
        if (!option.children?.length) {
          return (
            <Option
              className={values?.includes('all') ? 'ant-select-item-option-selected' : ''}
              key={option.value}
              value={option.value}
              label={option.label}
            >
              <OptionContentWrapper>
                <OptionLabel>{option.label}</OptionLabel>
                {values?.includes('all') && (
                  <SelectArrow className={'ant-select-item-option-state'}>
                    <UiKit.icons.Check />
                  </SelectArrow>
                )}
              </OptionContentWrapper>
            </Option>
          );
        }
        return (
          <OptionGroup key={option.value} label={option.label} value={option.value}>
            {option.children.map(child => (
              <Option
                className={values?.includes('all') ? 'ant-select-item-option-selected' : ''}
                key={child.value}
                value={child.value}
                label={child.label}
              >
                <OptionContentWrapper>
                  <OptionLabel>{child.label}</OptionLabel>
                  {values?.includes('all') && (
                    <SelectArrow className={'ant-select-item-option-state'}>
                      <UiKit.icons.Check />
                    </SelectArrow>
                  )}
                </OptionContentWrapper>
              </Option>
            ))}
          </OptionGroup>
        );
      })}
    </StyledSelect>
  );
}

const StyledSelect = styled(AntSelect)`
  &.ant-select {
    width: 100%;

    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          &:hover {
            .ant-select-selector {
              border-color: ${colorsPalette.dustyBlue500};
            }
          }
        }
      }
    }
  }

  &.ant-select-focused {
    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          .ant-select-selector {
            border-color: ${colorsPalette.dustyBlue500};
          }
        }
      }
    }
  }
` as typeof AntSelect;

const OptionContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const OptionLabel = styled.div``;
const SelectArrow = styled.span``;

const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

export default MultiSelect;
